import isTransientNetworkError from '@/utils/platform/isTransientNetworkError';

import pollRequest, { PollStatus } from './pollRequest';
import { type PollResponse } from './pollRequest';

type PollUntilCompletedOptions = {
  url: string;
  initialInterval?: number;
  maxInterval?: number;
  backoffFactor?: number;
  maxAttempts?: number;
  maxConsecutiveNetworkFailures?: number;
};

const sleep = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const pollUntilCompleted = async ({
  url,
  initialInterval = 1000,
  maxInterval = 8000,
  backoffFactor = 1.5,
  maxAttempts = 150,
  maxConsecutiveNetworkFailures = 20,
}: PollUntilCompletedOptions): Promise<PollResponse> => {
  let attempts = 0;
  let consecutiveNetworkFailures = 0;
  let currentInterval = initialInterval;

  while (attempts < maxAttempts) {
    // eslint-disable-next-line no-await-in-loop
    await sleep(currentInterval);

    let pollingResult: PollResponse | undefined;
    try {
      // eslint-disable-next-line no-await-in-loop
      pollingResult = await pollRequest(url);
    } catch (error) {
      // Backgrounded WebViews cancel in-flight requests as transport-level
      // failures. Retry without consuming an attempt; only give up after a
      // sustained run of failures (a genuine outage).
      if (!isTransientNetworkError(error)) {
        throw error;
      }
      consecutiveNetworkFailures++;
      if (consecutiveNetworkFailures >= maxConsecutiveNetworkFailures) {
        throw error;
      }
      currentInterval = initialInterval;
    }

    if (pollingResult) {
      consecutiveNetworkFailures = 0;
      attempts++;

      if (pollingResult.response.status === PollStatus.Failed) {
        throw new Error(
          `Workflow execution failed: ${JSON.stringify(pollingResult.response)}`
        );
      }

      if (pollingResult.response.status === PollStatus.Completed) {
        return pollingResult;
      }

      currentInterval = Math.min(currentInterval * backoffFactor, maxInterval);
    }
  }

  throw new Error('Polling timeout: Maximum attempts reached');
};

export default pollUntilCompleted;
